import configureGetCreateTimeInterval from '@utils/timeInterval';
import { axisBottom, AxisScale } from 'd3-axis';
import { ScaleTime } from 'd3-scale';
import { select } from 'd3-selection';
import moment from 'moment';
import { useContext, useEffect, useRef } from 'react';

import { DateRange } from '../../types';
import { GraphContext } from './GraphDataProvider';
import { DataEntry } from './types';
import { calculateDateRange } from './utils';

const DEFAULT_INTERVAL_TYPE = configureGetCreateTimeInterval({
    days: calculateDateRange('day'),
    weeks: calculateDateRange('week'),
    months: calculateDateRange('month'),
    quarters: calculateDateRange('month'),
    years: calculateDateRange('year'),
});

const DEFAULT_TIME_DIFF = configureGetCreateTimeInterval<moment.unitOfTime.Diff>({
    days: 'day',
    weeks: 'week',
    months: 'month',
    quarters: 'month',
    years: 'year',
});

export default function XAxis({
    dateRange,
    selectedElement,
    timeIntervalType = DEFAULT_INTERVAL_TYPE,
    tickFormatter = () => () => '',
    timeDiff = DEFAULT_TIME_DIFF,
    shouldHideTickLine = false,
}: {
    dateRange: DateRange;
    selectedElement?: DataEntry | Date;
    timeIntervalType?: (d: DateRange) => (start: Date, end: Date) => Date[];
    timeDiff?: (d: DateRange) => moment.unitOfTime.Diff;
    tickFormatter: (d: DateRange) => (date: Date) => string;
    shouldHideTickLine?: boolean;
}) {
    const {
        xScale,
        dimensions: { margin, height, widthWithoutMargin },
    } = useContext(GraphContext);
    const axisRef = useRef<SVGSVGElement | null>(null);
    const axisLine = useRef(null);

    const x = xScale as ScaleTime<number, number>;

    const interval = (timeIntervalType || DEFAULT_INTERVAL_TYPE)(dateRange);

    useEffect(() => {
        if (axisRef && axisRef.current) {
            const el = select<SVGSVGElement, Date>(axisRef.current);
            const xAxis = axisBottom<Date>(x as AxisScale<Date>)
                .tickValues(interval(x.domain()[0], x.domain()[1]))
                .tickFormat(tickFormatter(dateRange));
            el.call(xAxis);
            el.select('.domain').remove();
            el.selectAll<SVGSVGElement, Date>('.tick').attr('class', (d) =>
                selectedElement &&
                moment(d).diff(
                    selectedElement as Date,
                    (timeDiff || DEFAULT_TIME_DIFF)(dateRange),
                ) === 0
                    ? 'tick active'
                    : 'tick',
            );
            el.selectAll('.tick text').attr('x', 4).attr('dy', 10);

            if (shouldHideTickLine) {
                el.selectAll('.tick').selectAll('line').remove();
            }
        }

        if (axisLine.current) {
            select(axisLine.current).attr('x0', 0).attr('x1', widthWithoutMargin).attr('y0', 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [axisRef, height, margin, x]);

    return (
        <g transform={`translate(0 ${height})`}>
            <g ref={axisRef} className="axis xAxis" textAnchor="start" />
            <line ref={axisLine} className="axisLine" />
        </g>
    );
}
